/**
 * Messages API
 * 
 * Handles club chat and messaging functionality.
 */

import { apiGet, apiPost, apiPatch, apiDelete, SubscriptionLevel, type ApiRequestOptions } from '../api';

/**
 * #989 emoji reactions — the fixed 12-emoji palette (Seth 2026-08-11 decision).
 *
 * MUST match `REACTION_PALETTE` in `backend/src/routes/rooms.ts` verbatim,
 * INCLUDING order — the server's aggregation returns each message's reactions
 * palette-ordered, and the picker renders these swatches in this order. The
 * server is the single enforcement authority (Zod literal union); this constant
 * is the client render list + a compile-time union for the API helpers below.
 * Frozen so no caller can mutate the shared list. This is a transport-clean
 * constant (no React) so it is safe for the mobile symlink consumer.
 */
export const REACTION_PALETTE = [
  '👍', '❤️', '😂', '😮', '😢', '🔥', '🎉', '💪', '👏', '🙌', '⚡', '🏆',
] as const;

export type ReactionEmoji = (typeof REACTION_PALETTE)[number];

/**
 * #989: one aggregated reaction group as returned by the backend — the emoji,
 * how many users picked it, and whether the requesting viewer is one of them
 * (`mine` drives the chip highlight). Mirrors the `ReactionGroup` the
 * `rooms.ts` aggregation emits (`{ emoji, count, mine }`). The field is ALWAYS
 * present on server responses (empty array when none); `?` on `Message.reactions`
 * is purely for pre-#989 cached/mocked payloads (a distinct `undefined` state).
 */
export interface MessageReaction {
  emoji: ReactionEmoji;
  count: number;
  mine: boolean;
}

/**
 * #989: a single reactor in the lazy reactor-list peek. `display_name` is
 * already resolved server-side via `getPublicDisplayName` honoring
 * `public_name_mode` (#2779) — the client never re-derives a name.
 */
export interface ReactionUser {
  id: string;
  display_name: string;
}

export interface Message {
  id: string;
  club_id: string;
  room_id?: string;  // Added for room-based messaging (#790)
  user_id: string;
  user_name: string;
  // user_level removed - see #785
  subscription_level: SubscriptionLevel | null;
  profile_image_url: string | null;
  club_role: string;
  body: string;
  image_url?: string;
  image_urls?: string[] | null;
  created_at: number;
  // #1445 leader announcements: true when this message is a club announcement.
  // The 24h pin is derived client-side from is_announcement && created_at.
  // Optional for backward compat with pre-#1445 cached/mocked message payloads.
  is_announcement?: boolean;
  // #989 emoji reactions: palette-ordered aggregation of this message's
  // reactions. ALWAYS present on server responses (empty array when none);
  // optional here purely for pre-#989 cached/mocked payloads (undefined =
  // "unknown", treated as [] by consumers). Present on messages[], the
  // pinned_announcement, and the pinned_message objects alike.
  reactions?: MessageReaction[];
}

/**
 * Response envelope for GET /rooms/:roomId/messages.
 *
 * `pinned_announcement` (#2674): the latest non-deleted announcement in the
 * room within the last 24h, resolved SERVER-side so the pinned banner renders
 * on cold-open even when the announcement is more than one page (~50 messages)
 * deep — the paging blind spot the client-side derivation can't see.
 *
 * ALWAYS present in current server responses (`backend/src/routes/rooms.ts`
 * ~L333): the value is a message only for the true club lobby WITH a sub-24h
 * announcement, and `null` in every other case (non-lobby room, or no current
 * pin). The `?` marks the field optional purely for pre-#2674 clients/caches,
 * where it is `undefined` — a distinct third state that makes consumers fall
 * back to deriving the pin from loaded pages (the fast path). Consumers weigh
 * this field against the derived pin (newest valid candidate wins) via
 * `pickPinnedAnnouncement`; see `frontend/src/lib/pinnedAnnouncement.ts`.
 */
export interface GetRoomMessagesResponse {
  messages: Message[];
  pinned_announcement?: Message | null;
  /**
   * #2708 TRUE pin: the room's `rooms.pinned_message_id` resolved to a full
   * message (same serialize shape as `pinned_announcement`, carrying its own
   * `reactions` and a `getPublicDisplayName`-resolved sender), or `null` when
   * there is no pin / the target was soft-deleted. Distinct from the derived
   * 24h `pinned_announcement`: persistent, silent, one per room, present on
   * EVERY room type (not lobby-only). ALWAYS present in current server
   * responses (`backend/src/routes/rooms.ts` GET /rooms/:roomId/messages —
   * `pinned_message: pinnedMessage ? ... : null`); `?` marks it optional only
   * for pre-#2708 clients/caches, where it is `undefined`.
   */
  pinned_message?: Message | null;
}

/** #989 POST/DELETE reaction write response. `reactions` is the freshly
 * re-aggregated palette-ordered list for the message (the server's authority
 * the client writes back into cache on settle). Mirrors the `rooms.ts`
 * reaction-write handlers: `{ ok: true, reactions: [...] }`. */
export interface ReactionWriteResponse {
  ok: boolean;
  reactions: MessageReaction[];
}

/** #989 reactor-list peek response. Bounded fixed cap + `has_more` (NOT a
 * cursor) — mirrors `rooms.ts` GET .../reactions/:emoji/users. */
export interface ReactionUsersResponse {
  users: ReactionUser[];
  has_more: boolean;
}

/** #2708 pin write response (PATCH/DELETE /rooms/:roomId/pin → `{ ok: true }`). */
export interface PinWriteResponse {
  ok: boolean;
}

export interface SendMessagePayload {
  body: string;
  image_url?: string;
  image_urls?: string[];
  // #1445: set true to post this message as a club announcement (leaders/owners
  // of Essential+ clubs only; server enforces the gate).
  is_announcement?: boolean;
  /**
   * #1481 Layer 3 idempotency, wired client-side in #2381: a stable UUID
   * identifying the LOGICAL send. The server (rooms.ts SendMessageSchema)
   * short-circuits on an existing (user_id, client_message_id, room_id)
   * match, so re-sending after a timed-out-but-committed POST returns the
   * existing row instead of inserting a duplicate. Send hooks mint it via
   * createClientMessageIdMinter (lib/deduplication) — the id must be REUSED
   * across retries of the same logical message, never per attempt.
   */
  client_message_id?: string;
}

export const messagesApi = {
  // Room-based messaging (#790).
  // The club-scoped getClubMessages / sendMessage helpers were removed with the
  // dead POST/GET /clubs/:clubId/messages routes (#2666); all chat send/read
  // goes through the room-scoped endpoints below.

  /**
   * Get messages for a room
   * GET /rooms/:roomId/messages?limit=50&before=timestamp&before_id=uuid
   *
   * `beforeId` (#2219) is the second half of the composite keyset cursor —
   * pass the oldest rendered message's id alongside its created_at so
   * same-second messages split across a page boundary aren't dropped.
   * Backend treats `before` alone as the legacy strict-< cursor.
   */
  getRoomMessages: (roomId: string, limit: number = 50, before?: number, beforeId?: string) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (before !== undefined) {
      params.append('before', before.toString());
      if (beforeId !== undefined) {
        params.append('before_id', beforeId);
      }
    }
    // #2674: response carries `pinned_announcement` alongside `messages`.
    return apiGet<GetRoomMessagesResponse>(`/rooms/${roomId}/messages?${params.toString()}`);
  },

  /**
   * Send message to room
   * POST /rooms/:roomId/messages
   *
   * `options` (idempotency key / abort signal) pass-through added for the
   * native sender (#2066 Slice C). Additive: web's useSendRoomMessage calls
   * apiPost directly and no web code consumes this function.
   */
  sendRoomMessage: (roomId: string, payload: SendMessagePayload, options?: ApiRequestOptions) =>
    apiPost<{ message: Message }>(`/rooms/${roomId}/messages`, payload, options),

  /**
   * Delete a message (own messages or moderator privileges)
   * DELETE /clubs/:clubId/messages/:messageId
   * Issue #920
   *
   * Envelope is { ok } (the clubs.ts DELETE handler), NOT the { success }
   * most mutations return — was typed { success } until the #2066 Slice D
   * contract audit caught the lie. No caller reads the field.
   */
  deleteMessage: (clubId: string, messageId: string) =>
    apiDelete<{ ok: boolean }>(`/clubs/${clubId}/messages/${messageId}`),

  // ─── #989 emoji reactions ─────────────────────────────────────────────────

  /**
   * Add (toggle-on) the viewer's reaction to a message.
   * POST /rooms/:roomId/messages/:messageId/reactions   { emoji }
   *
   * Idempotent server-side (UNIQUE(message_id,user_id,emoji) → repeat is a
   * 200 no-op). Returns the freshly re-aggregated palette-ordered `reactions`
   * for the message — the authoritative state the caller writes back on settle.
   * The emoji must be a palette member (server rejects any other with 400).
   */
  addReaction: (roomId: string, messageId: string, emoji: ReactionEmoji) =>
    apiPost<ReactionWriteResponse>(
      `/rooms/${roomId}/messages/${messageId}/reactions`,
      { emoji },
    ),

  /**
   * Remove (toggle-off) the viewer's own reaction.
   * DELETE /rooms/:roomId/messages/:messageId/reactions/:emoji
   *
   * Own-reaction only (server scopes the WHERE by auth user_id). Absent =
   * 200 no-op. Returns the re-aggregated `reactions` for the message. The
   * emoji is path-segment encoded (multi-byte palette glyphs).
   */
  removeReaction: (roomId: string, messageId: string, emoji: ReactionEmoji) =>
    apiDelete<ReactionWriteResponse>(
      `/rooms/${roomId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
    ),

  /**
   * Lazy reactor-list peek for one emoji on one message (fetched on chip tap /
   * hover, NOT part of the page aggregation).
   * GET /rooms/:roomId/messages/:messageId/reactions/:emoji/users
   *
   * Bounded fixed cap (100) + `has_more`. Names are `getPublicDisplayName`-
   * resolved server-side (#2779) — an anonymous reactor leaks no name.
   */
  getReactionUsers: (roomId: string, messageId: string, emoji: ReactionEmoji) =>
    apiGet<ReactionUsersResponse>(
      `/rooms/${roomId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/users`,
    ),

  // ─── #2708 pin a message (TRUE pin — rooms.pinned_message_id pointer) ──────

  /**
   * Pin (or replace the room's pin with) a message. Leader/owner only —
   * enforced server-side (Rule 48); clients gate the affordance on the
   * `can_pin` room hint. One pin per room (idempotent pointer set).
   * PATCH /rooms/:roomId/pin   { message_id }
   */
  pinMessage: (roomId: string, messageId: string) =>
    apiPatch<PinWriteResponse>(`/rooms/${roomId}/pin`, { message_id: messageId }),

  /**
   * Unpin the room's pinned message (clears the pointer). Leader/owner only.
   * Idempotent (unpinning an unpinned room is a 200 no-op).
   * DELETE /rooms/:roomId/pin
   */
  unpinMessage: (roomId: string) =>
    apiDelete<PinWriteResponse>(`/rooms/${roomId}/pin`),
};
